// Unified API client for the RankVine embedded app.
//
// AUTH BRIDGE (the crux): every request goes through `request()`, which pulls a
// fresh Shopify App Bridge session token via the injected `getToken()` and sends
// it as `Authorization: Bearer <token>`. The backend verifies the JWT (HS256 with
// the app client_secret), derives the tenant/store from the token's `dest`, and
// never trusts a client-declared tenant. Tokens are short-lived, so we fetch one
// per request rather than caching — App Bridge mints them cheaply.
//
// The token getter is injected (see ApiProvider) so this module has no direct
// dependency on App Bridge and stays unit-testable.

import type {
  ApproveResult,
  AutomationOut,
  AutomationUpdateIn,
  ContentAsset,
  ContentAssetDetail,
  CursorPage,
  MeStore,
  Opportunity,
  PublishFunnel,
  RevenueDailyReport,
  StoreConfig,
  StoreConfigOut,
  StoreDetail,
  Task,
} from "./types";

const API_PREFIX = "/api/v1";

// Same-origin by default (dist/ served from the backend host). Override with
// VITE_API_BASE_URL only when the frontend is hosted on a different origin.
const BASE_URL = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

export type TokenGetter = () => Promise<string>;

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  constructor(status: number, detail: string) {
    super(`API ${status}: ${detail}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

type QueryValue = string | number | boolean | null | undefined;

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH";
  query?: Record<string, QueryValue>;
  body?: unknown;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const qs = new URLSearchParams();
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== null && value !== undefined && value !== "") {
        qs.append(key, String(value));
      }
    }
  }
  const suffix = qs.toString();
  return `${BASE_URL}${API_PREFIX}${path}${suffix ? `?${suffix}` : ""}`;
}

export function createApiClient(getToken: TokenGetter) {
  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = "GET", query, body } = options;
    let token: string;
    try {
      token = await getToken();
    } catch (err) {
      throw new ApiError(0, `Could not obtain a session token: ${String(err)}`);
    }

    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    if (body !== undefined) headers["Content-Type"] = "application/json";

    let res: Response;
    try {
      res = await fetch(buildUrl(path, query), {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      // Network / CORS / abort — surface as a status-0 ApiError so callers render
      // a friendly message instead of an unhandled rejection.
      throw new ApiError(0, `Network error: ${String(err)}`);
    }

    if (!res.ok) {
      // FastAPI errors are `{ detail: string | object }`. Be defensive.
      let detail = res.statusText || "Request failed";
      try {
        const data = await res.json();
        if (data && typeof data.detail === "string") detail = data.detail;
        else if (data?.detail) detail = JSON.stringify(data.detail);
      } catch {
        /* non-JSON body — keep statusText */
      }
      throw new ApiError(res.status, detail);
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return {
    request,

    // --- Diagnostics / store ---
    getMyStore: () => request<MeStore>("/me/store"),
    getStore: (storeId: string) => request<StoreDetail>(`/stores/${storeId}`),
    updateStoreConfig: (storeId: string, body: Partial<StoreConfig>) =>
      request<StoreConfigOut>(`/stores/${storeId}/config`, { method: "PATCH", body }),
    updateAutomation: (storeId: string, body: AutomationUpdateIn) =>
      request<AutomationOut>(`/stores/${storeId}/automation`, { method: "PATCH", body }),

    // --- Opportunities ---
    listOpportunities: (params: { store_id?: string; min_score?: number; limit?: number; cursor?: string } = {}) =>
      request<CursorPage<Opportunity>>("/opportunities", { query: params }),

    // --- Content ---
    listContents: (params: { store_id?: string; content_type?: string; limit?: number; cursor?: string } = {}) =>
      request<CursorPage<ContentAsset>>("/contents", { query: params }),
    getContent: (id: string) => request<ContentAssetDetail>(`/contents/${id}`),
    approveContent: (id: string) =>
      request<ApproveResult>(`/contents/${id}/approve`, { method: "POST" }),

    // --- Tasks ---
    listTasks: (params: { store_id?: string; status?: string; queue_name?: string; limit?: number; cursor?: string } = {}) =>
      request<CursorPage<Task>>("/tasks", { query: params }),

    // --- Reports ---
    getPublishFunnel: (storeId: string) =>
      request<PublishFunnel>("/reports/publish-funnel", { query: { store_id: storeId } }),
    getRevenueDaily: (params: {
      store_id: string;
      model?: string;
      channel?: string;
      date_from?: string;
      date_to?: string;
    }) => request<RevenueDailyReport>("/reports/revenue-daily", { query: params }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
